import { useEffect, useRef, useState } from 'react';
import { FileUp, Loader2, Calculator, History, FilePlus2, AlertTriangle, CheckCircle2, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/apiService';
import CabecalhoNota from '../components/precificacao/CabecalhoNota';
import ItensNota from '../components/precificacao/ItensNota';
import PainelFrete from '../components/precificacao/PainelFrete';
import ResultadoCusto from '../components/precificacao/ResultadoCusto';
import HistoricoPrecificacao from '../components/precificacao/HistoricoPrecificacao';
import AvisoSugestaoIA from '../components/precificacao/AvisoSugestaoIA';
import ExplicacaoOrigem from '../components/precificacao/ExplicacaoOrigem';
import CorrespondenciaErp from '../components/precificacao/CorrespondenciaErp';
import VinculosFornecedor from '../components/precificacao/VinculosFornecedor';

// Pedidos > Precificação — .clinerules/19-precificacao-pedidos.md
// Fluxo: importar XML da NF-e → corresponder produtos ao ERP (obrigatório) → conferir dados (IA sugere
// ST/antecipação) → frete FOB/CT-e → "Salvar e Calcular" (números do backend + explicação da IA, histórico).

const FRETE_INICIAL = {
  valorTotal: 0,
  icmsCTe: 0,
  percentualNota: 100,
  rateio: 'valor',
  componentes: [],
  cte: null,
  avisos: [],
  liberadoManual: false,
};

const FRETE_ZERADO = { valorTotal: 0, icmsCTe: 0, percentualNota: 100, rateio: 'valor' };

export default function Precificacao() {
  const inputNfeRef = useRef(null);
  const [aba, setAba] = useState('nova');
  const [importando, setImportando] = useState(false);
  const [calculando, setCalculando] = useState(false);

  const [id, setId] = useState(null);
  const [nota, setNota] = useState(null);
  const [loja, setLoja] = useState(null);
  const [itens, setItens] = useState([]);
  const [ia, setIa] = useState(null);
  const [regime, setRegime] = useState('');
  const [aliquotaInternaCE, setAliquotaInternaCE] = useState(20);
  const [frete, setFrete] = useState(FRETE_INICIAL);
  const [resultado, setResultado] = useState(null);
  const [desatualizado, setDesatualizado] = useState(false);
  const [avisoErp, setAvisoErp] = useState('');
  const [solicitacoesPendentes, setSolicitacoesPendentes] = useState(0);

  useEffect(() => {
    let cancelado = false;
    apiService.precificacao.solicitacoes({ status: 'pendente' })
      .then(resp => { if (!cancelado) setSolicitacoesPendentes(resp.data.total || 0); })
      .catch(() => { /* contador é só informativo */ });
    return () => { cancelado = true; };
  }, []);

  function limpar() {
    setId(null);
    setNota(null);
    setLoja(null);
    setItens([]);
    setIa(null);
    setRegime('');
    setAliquotaInternaCE(20);
    setFrete(FRETE_INICIAL);
    setResultado(null);
    setDesatualizado(false);
    setAvisoErp('');
  }

  /** Qualquer edição depois do cálculo deixa o resultado marcado como desatualizado. */
  function marcarAlteracao(acao) {
    return (...args) => {
      acao(...args);
      if (resultado) setDesatualizado(true);
    };
  }

  const alterarItem = marcarAlteracao((indice, chave, valor) => {
    setItens(prev => prev.map(item => (item.indice === indice ? { ...item, [chave]: valor } : item)));
  });

  // Vínculo com o ERP mudou (vinculado, desvinculado, liberação solicitada).
  const atualizarVinculoItem = marcarAlteracao((indice, campos) => {
    setItens(prev => prev.map(item => (item.indice === indice ? { ...item, ...campos } : item)));
  });

  async function importarNFe(arquivo) {
    if (!arquivo) return;
    setImportando(true);
    try {
      const resp = await apiService.precificacao.importarNFe(arquivo);
      const dados = resp.data.data;
      limpar();
      setNota(dados.nota);
      setLoja(dados.loja);
      setItens(dados.itens);
      setIa(dados.ia);
      setRegime(dados.loja?.regime || '');
      setAliquotaInternaCE(dados.parametros?.aliquotaInternaCE ?? 20);
      setAvisoErp(dados.avisoErp || '');
      if (dados.avisoIa) toast.error(dados.avisoIa, { duration: 6000 });
      else toast.success(`NF-e ${dados.nota.numero} importada com ${dados.itens.length} item(ns). Confira os dados.`);
    } catch (error) {
      console.error('❌ Erro ao importar NF-e:', error);
    } finally {
      setImportando(false);
      if (inputNfeRef.current) inputNfeRef.current.value = '';
    }
  }

  // Recebe o estado por parâmetro (chamada longa: não confiar na closure).
  async function salvarECalcular(estado) {
    if (!estado.regime) {
      toast.error('Escolha o regime tributário da loja antes de calcular.');
      return;
    }
    setCalculando(true);
    try {
      const freteAberto = estado.nota.freteFicaComALoja || estado.frete.liberadoManual;
      const resp = await apiService.precificacao.calcularESalvar({
        id: estado.id,
        nota: estado.nota,
        loja: estado.loja,
        itens: estado.itens,
        frete: freteAberto ? estado.frete : { ...estado.frete, ...FRETE_ZERADO },
        parametros: { regime: estado.regime, aliquotaInternaCE: estado.aliquotaInternaCE },
      });
      const dados = resp.data.data;
      setResultado(dados);
      setDesatualizado(false);
      if (dados.id) setId(dados.id);
      if (dados.avisoSalvar) toast.error(dados.avisoSalvar, { duration: 6000 });
      else toast.success('Cálculo feito e salvo no histórico.');
    } catch (error) {
      console.error('❌ Erro ao calcular precificação:', error);
    } finally {
      setCalculando(false);
    }
  }

  async function abrirDoHistorico(idHistorico) {
    try {
      const resp = await apiService.precificacao.buscar(idHistorico);
      const dados = resp.data.data;
      const snap = dados.snapshot;
      if (!snap?.nota) {
        toast.error('Esta precificação não tem os dados completos para reabrir.');
        return;
      }
      setId(dados.id);
      setNota(snap.nota);
      setLoja(snap.loja);
      setItens(snap.itens || []);
      setIa(null);
      setRegime(snap.parametros?.regime || dados.regime);
      setAliquotaInternaCE(snap.parametros?.aliquotaInternaCE ?? 20);
      setFrete({ ...FRETE_INICIAL, ...snap.frete });
      setResultado(dados.resultado);
      setDesatualizado(false);
      setAba('nova');
    } catch (error) {
      console.error('❌ Erro ao abrir precificação:', error);
    }
  }

  const estadoAtual = { id, nota, loja, itens, regime, aliquotaInternaCE, frete };
  const faltamVincular = itens.filter(i => !i.vinculo).length;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Precificação</h1>
          <p className="text-sm text-gray-500">Custo do produto a partir do XML da nota de compra, com frete e impostos.</p>
        </div>
        <div className="flex rounded-lg border border-gray-200 bg-white p-1">
          <button
            type="button"
            onClick={() => setAba('nova')}
            className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-md ${aba === 'nova' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            <FilePlus2 className="w-4 h-4" /> Precificar
          </button>
          <button
            type="button"
            onClick={() => setAba('historico')}
            className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-md ${aba === 'historico' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            <History className="w-4 h-4" /> Histórico
          </button>
          <button
            type="button"
            onClick={() => setAba('vinculos')}
            className={`relative flex items-center gap-1 px-3 py-1.5 text-sm rounded-md ${aba === 'vinculos' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            <Link2 className="w-4 h-4" /> Vínculos
            {solicitacoesPendentes > 0 && (
              <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[11px] font-bold flex items-center justify-center">
                {solicitacoesPendentes}
              </span>
            )}
          </button>
        </div>
      </div>

      {aba === 'historico' && <HistoricoPrecificacao onAbrir={abrirDoHistorico} />}
      {aba === 'vinculos' && <VinculosFornecedor onPendentesMudou={setSolicitacoesPendentes} />}
      {aba === 'nova' && (
        <>
          <div className="bg-white border-2 border-dashed border-blue-200 rounded-xl p-5 flex flex-wrap items-center gap-4">
            <FileUp className="w-8 h-8 text-blue-500" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900">{nota ? 'Importar outra NF-e' : 'Importe o XML da NF-e de compra'}</p>
              <p className="text-sm text-gray-500">
                Os dados saem direto do XML. A IA sugere a classificação fiscal (ST / antecipação no CE) para você conferir.
              </p>
            </div>
            <input ref={inputNfeRef} type="file" accept=".xml" className="hidden" onChange={e => importarNFe(e.target.files?.[0])} />
            <button
              type="button"
              onClick={() => inputNfeRef.current?.click()}
              disabled={importando || calculando}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
            >
              {importando ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
              {importando ? 'Lendo a nota e consultando a IA…' : 'Selecionar XML da NF-e'}
            </button>
          </div>

          {nota && (
            <>
              <CabecalhoNota
                nota={nota}
                loja={loja}
                regime={regime}
                onRegime={marcarAlteracao(setRegime)}
                aliquotaInternaCE={aliquotaInternaCE}
                onAliquotaInternaCE={marcarAlteracao(setAliquotaInternaCE)}
                ia={ia}
              />

              <CorrespondenciaErp nota={nota} itens={itens} onAtualizarItem={atualizarVinculoItem} avisoErp={avisoErp} />

              <ExplicacaoOrigem ufOrigem={nota.emitente.uf} regime={regime} aliquotaInternaCE={aliquotaInternaCE} />

              <ItensNota key={nota.chave} itens={itens} onAlterar={alterarItem} />

              <PainelFrete nota={nota} frete={frete} onFrete={marcarAlteracao(setFrete)} />

              <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
                <AvisoSugestaoIA compacto />
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-gray-600">
                    {faltamVincular > 0
                      ? <span className="flex items-center gap-1 text-blue-700 font-medium"><Link2 className="w-4 h-4" />Faltam {faltamVincular} item(ns) para corresponder ao ERP — o cálculo libera quando todos estiverem vinculados.</span>
                      : id
                      ? <span className="flex items-center gap-1"><CheckCircle2 className="w-4 h-4 text-green-600" />Salvo no histórico (nº {id}). Recalcular atualiza o mesmo registro.</span>
                      : 'Confira os dados acima. O cálculo é salvo no histórico automaticamente.'}
                  </p>
                  <button
                    type="button"
                    onClick={() => salvarECalcular(estadoAtual)}
                    disabled={calculando || importando || faltamVincular > 0}
                    title={faltamVincular > 0 ? `Faltam ${faltamVincular} item(ns) para corresponder ao ERP` : undefined}
                    className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:opacity-60 shadow"
                  >
                    {calculando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Calculator className="w-5 h-5" />}
                    {calculando ? 'Calculando e explicando…' : 'Salvar e Calcular'}
                  </button>
                </div>
              </div>

              {resultado && (
                <>
                  {desatualizado && (
                    <p className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      <AlertTriangle className="w-4 h-4" />
                      Você alterou dados depois do cálculo. Clique em "Salvar e Calcular" de novo para atualizar.
                    </p>
                  )}
                  <div className={desatualizado ? 'opacity-50' : ''}>
                    <ResultadoCusto resultado={resultado} itens={itens} />
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
